import { hashKey, isServer, QueryClient } from '@tanstack/react-query';
import { BaseApiResponse } from '../Api';
import { hashKeyInScope, isIdentityIndependent, scopeOf, sessionOf } from './identity';

/**
 * Retry only what a retry can fix: a 5xx, or a failure without a status (the network). A 401, 404
 * or validation error answers the same the second time, and retrying it only delays the error
 * page. The server never retries: the render waits for every attempt.
 */
export function shouldRetryQuery(failureCount: number, error: unknown, onServer: boolean = isServer): boolean {
    if (onServer) return false;
    const statusCode = (error as Partial<BaseApiResponse> | null)?.statusCode ?? 500;
    return statusCode >= 500 && failureCount < 3;
}

/** The QueryClient every runtime, the embed app and the tests start from. */
export function createQueryClient(): QueryClient {
    const queryClient: QueryClient = new QueryClient({
        defaultOptions: {
            queries: {
                retry: shouldRetryQuery,
                // Every key is hashed with the identity this client's own session entry names, so
                // the cache is kept apart per identity while keys stay plain in code, devtools and
                // logs. query-core hashes through defaultQueryOptions everywhere (getQueryData,
                // setQueryData, ensureQueryData, observers), hydrate() stores a server-rendered
                // entry under the server's hash, and prefix invalidations look at the raw key --
                // so all three keep working. The identity-independent check comes first: reading
                // the session is itself a lookup that hashes the session key, and must not recurse.
                queryKeyHashFn: (queryKey) => (isIdentityIndependent(queryKey) ? hashKey(queryKey) : hashKeyInScope(queryKey, scopeOf(sessionOf(queryClient))))
            },
            mutations: { retry: false }
        }
    });
    return queryClient;
}
